import { Card, Divider, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Job } from "generated/graphql";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Tooltip as MuiTooltip } from "@mui/material";

const toNumber = (value?: number | string | null) => Number(value ?? 0);
const toDollars = (value?: number | string | null) => toNumber(value) / 100;

const formatPercent = (value: number) => `${Math.round(value)}%`;

function MetricCard({
  label,
  value,
  color = "dark",
  tooltip,
}: {
  label: string;
  value: string;
  color?: "dark" | "success" | "error" | "info" | "text";
  tooltip?: string;
}) {
  return (
    <MDBox
      p={1.75}
      borderRadius="lg"
      sx={{
        background: "linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(241,245,249,0.96) 100%)",
        border: "1px solid rgba(148,163,184,0.18)",
      }}
    >
      <MDBox display="flex" alignItems="center">
        <MDTypography variant="caption" color="text" textTransform="uppercase" letterSpacing="0.06em">
          {label}
        </MDTypography>
        {tooltip ? <InlineTooltip title={tooltip} /> : null}
      </MDBox>
      <MDTypography variant="h5" fontWeight="medium" color={color} lineHeight={1.15}>
        {value}
      </MDTypography>
    </MDBox>
  );
}

function InlineTooltip({ title }: { title: string }) {
  return (
    <MuiTooltip
      title={title}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: "0.72rem",
            px: 1,
            py: 0.6,
            maxWidth: 220,
          },
        },
      }}
    >
      <MDBox component="span" display="inline-flex" ml={0.5} sx={{ verticalAlign: "middle", cursor: "help" }}>
        <InfoOutlinedIcon sx={{ fontSize: 14, color: "#64748b" }} />
      </MDBox>
    </MuiTooltip>
  );
}

function CostProgressTooltip({ active, payload }: TooltipProps<ValueType, NameType>) {
  if (!active || !payload?.length) {
    return null;
  }

  const values = payload.reduce<Record<string, number>>((accumulator, entry) => {
    accumulator[String(entry.dataKey)] = Number(entry.value ?? 0);
    return accumulator;
  }, {});

  return (
    <MDBox
      px={1}
      py={0.75}
      borderRadius="md"
      sx={{
        backgroundColor: "rgba(15, 23, 42, 0.94)",
        minWidth: 140,
      }}
    >
      <MDTypography variant="caption" color="white" fontWeight="medium" display="block" mb={0.4}>
        Proposed Cost
      </MDTypography>
      <MDTypography variant="caption" color="white" display="block">
        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
          (values.entered ?? 0) + (values.remaining ?? 0)
        )}
      </MDTypography>
      <MDTypography variant="caption" color="white" fontWeight="medium" display="block" mt={0.7} mb={0.4}>
        Entered
      </MDTypography>
      <MDTypography variant="caption" color="white" display="block">
        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(values.entered ?? 0)}
      </MDTypography>
      <MDTypography variant="caption" color="white" fontWeight="medium" display="block" mt={0.7} mb={0.4}>
        Remaining
      </MDTypography>
      <MDTypography variant="caption" color="white" display="block">
        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(values.remaining ?? 0)}
      </MDTypography>
      <MDTypography variant="caption" color="white" fontWeight="medium" display="block" mt={0.7} mb={0.4}>
        Over Budget
      </MDTypography>
      <MDTypography variant="caption" color="white" display="block">
        {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(values.overrun ?? 0)}
      </MDTypography>
    </MDBox>
  );
}

export default function JobCostProgressPanel({ job }: { job: Job }) {
  const proposedRevenue = toNumber(job.proposedOverallTotal);
  const proposedDirectCost = toNumber(job.proposedCostTotal);
  const actualDirectCost = toNumber(job.actualCostTotal);
  const actualProfit = proposedRevenue - actualDirectCost;
  const remainingCost = Math.max(proposedDirectCost - actualDirectCost, 0);
  const overrunCost = Math.max(actualDirectCost - proposedDirectCost, 0);
  const enteredCost = Math.min(actualDirectCost, proposedDirectCost);
  const progressPercent = proposedDirectCost > 0 ? (actualDirectCost / proposedDirectCost) * 100 : 0;
  const profitColor = actualProfit >= 0 ? "success" : "error";

  const chartData = [
    {
      name: "Costs",
      entered: toDollars(enteredCost),
      remaining: toDollars(remainingCost),
      overrun: toDollars(overrunCost),
    },
  ];

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDTypography variant="button" fontWeight="medium" sx={{ letterSpacing: "0.02em", lineHeight: 1.1 }}>
          Job Cost Progress
        </MDTypography>

        <Grid container spacing={1.5} mt={0.5} mb={2}>
          <Grid item xs={12} sm={6}>
            <MetricCard
              label="Total Costs"
              value={formatCentsToUSD(proposedDirectCost)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MetricCard
              label="Actual Costs"
              value={`${formatCentsToUSD(actualDirectCost)} / ${formatPercent(progressPercent)}`}
              color="info"
              tooltip="Entered cost and percent of proposed direct cost entered."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MetricCard
              label="Proposed Profit"
              value={formatCentsToUSD(job.proposedProfit)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MetricCard
              label="Actual Profit"
              value={formatCentsToUSD(actualProfit)}
              color={profitColor}
              tooltip="Calculated as total proposed amount minus total costs."
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <MDBox>
          <MDBox display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="button" fontWeight="medium">
                Direct Cost Progress
              </MDTypography>
              <InlineTooltip title="Proposed cost, entered cost, remaining cost, and any over-budget amount." />
            </MDBox>
            <MDTypography variant="button" color={progressPercent > 100 ? "error" : "text"}>
              {progressPercent.toFixed(0)}% entered
            </MDTypography>
          </MDBox>
          <ResponsiveContainer width="100%" height={110}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 2, right: 8, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis
                type="number"
                tickFormatter={(value) => `$${Number(value).toLocaleString("en-US")}`}
                stroke="#94a3b8"
                fontSize={12}
              />
              <YAxis type="category" dataKey="name" width={0} hide />
              <Tooltip content={<CostProgressTooltip />} cursor={{ fill: "rgba(148, 163, 184, 0.08)" }} />
              <Bar dataKey="entered" stackId="costs" fill="#0f766e" radius={[6, 0, 0, 6]} />
              <Bar dataKey="remaining" stackId="costs" fill="#cbd5e1" radius={[0, 6, 6, 0]} />
              <Bar dataKey="overrun" stackId="overrun" fill="#dc2626" radius={[6, 6, 6, 6]} />
            </BarChart>
          </ResponsiveContainer>
        </MDBox>
      </MDBox>
    </Card>
  );
}
